import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AppError,
  ClientRoomState,
  EnrichmentProgress,
  GameSettings,
  Guess,
} from '@hitster/shared';
import { clearResume, getSocket, loadResume, saveResume } from './socket.ts';

/**
 * Zentraler Zustandshalter der App.
 *
 * Der Server ist die einzige Quelle der Wahrheit: der Client haelt nur
 * den letzten empfangenen `room:state` und schickt Absichten hin. Kein
 * optimistisches Update -- bei einem Partyspiel mit Wetten waere
 * abweichender lokaler Zustand fatal.
 */

export interface LogEntry {
  id: number;
  at: number;
  key: string;
  params?: Record<string, string | number>;
}

/**
 * Obergrenze fuer Server-Antworten.
 *
 * Socket.IO-Acknowledgements haben von sich aus KEIN Zeitlimit: bleibt
 * die Antwort aus (Verbindungsabbruch im falschen Moment, Fehler vor
 * dem Ack), haengt das Promise fuer immer. Die UI, die darauf wartet,
 * bleibt dann dauerhaft im Ladezustand und reagiert auf keinen Klick
 * mehr. Deshalb settelt jede Anfrage garantiert.
 */
const ACK_TIMEOUT_MS = 45_000;

function withAckTimeout<T>(
  start: (settle: (value: T) => void) => void,
  onTimeout: () => T,
): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false;

    const settle = (value: T): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };

    const timer = setTimeout(() => settle(onTimeout()), ACK_TIMEOUT_MS);
    start(settle);
  });
}

export interface RoomApi {
  state: ClientRoomState | null;
  connected: boolean;
  error: AppError | null;
  clearError: () => void;
  log: LogEntry[];
  closedReason: string | null;

  createRoom: (name: string) => Promise<{ ok: boolean; error?: AppError; code?: string }>;
  joinRoom: (
    code: string,
    name: string,
    asSpectator?: boolean,
  ) => Promise<{ ok: boolean; error?: AppError }>;
  leaveRoom: () => void;
  /** Zwischen Mitspielen und Zuschauen wechseln (nur in der Lobby). */
  setPlaying: (playing: boolean) => void;

  updateSettings: (patch: Partial<GameSettings>) => void;
  selectPlaylist: (ref: string) => Promise<{ ok: boolean; error?: AppError }>;
  startGame: () => void;
  restartGame: () => void;
  /** Laufendes Spiel abbrechen (nur Host). */
  abortGame: () => void;
  kick: (playerId: string) => void;
  addLocalPlayer: (name: string) => Promise<{ ok: boolean; error?: AppError }>;
  removeLocalPlayer: (playerId: string) => void;
  advance: () => void;

  /** onBehalfOf: Host handelt fuer einen lokalen Mitspieler. */
  place: (slotIndex: number, onBehalfOf?: string) => void;
  guess: (guess: Guess, onBehalfOf?: string) => void;
  skip: (onBehalfOf?: string) => void;
  /** Aktuellen Song von vorn abspielen. */
  replay: () => void;
  commit: (onBehalfOf?: string) => void;
  claimSteal: (slotIndex: number, onBehalfOf?: string) => void;
  passSteal: (onBehalfOf?: string) => void;

  registerAudio: (deviceId: string) => void;
  unregisterAudio: () => void;
  setAudioEnabled: (enabled: boolean) => void;
  /** Server soll die Spotify-Verknuepfung dieses Clients neu einlesen. */
  syncSession: () => void;
}

export function useRoom(): RoomApi {
  const socket = useMemo(() => getSocket(), []);
  const [state, setState] = useState<ClientRoomState | null>(null);
  const [connected, setConnected] = useState(socket.connected);
  /**
   * Fehler tragen einen Zeitstempel, damit zwei gleiche Fehler
   * hintereinander den Auto-Ausblend-Timer neu starten.
   */
  const [error, setErrorState] = useState<(AppError & { at: number }) | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [closedReason, setClosedReason] = useState<string | null>(null);
  const logSeq = useRef(0);

  const setError = useCallback((next: AppError) => {
    setErrorState({ ...next, at: Date.now() });
  }, []);

  const clearError = useCallback(() => setErrorState(null), []);

  /**
   * Fehler sind fluechtige Hinweise, keine Zustaende.
   *
   * Ohne Auto-Ausblenden bleibt eine Meldung von einem fehlgeschlagenen
   * Versuch stehen, obwohl der naechste Versuch geklappt hat -- man
   * sieht dann gleichzeitig "gesperrt" und die geladene Playlist. Genau
   * das ist passiert.
   */
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setErrorState(null), 12_000);
    return () => clearTimeout(timer);
  }, [error]);

  // -------------------------------------------------------------------------
  // Verbindung und Events
  // -------------------------------------------------------------------------

  useEffect(() => {
    const onConnect = () => {
      setConnected(true);
      setClosedReason(null);

      // Nach Reload oder Netzwechsel automatisch wieder einsteigen.
      const resume = loadResume();
      if (!resume) return;

      socket.emit('room:resume', { code: resume.code, resumeToken: resume.resumeToken }, (res) => {
        if (res.ok) {
          saveResume(res.data);
        } else {
          // Raum existiert nicht mehr -> gespeicherte Sitzung wegwerfen.
          clearResume();
          setState(null);
        }
      });
    };

    const onDisconnect = () => setConnected(false);
    const onState = (next: ClientRoomState) => setState(next);
    const onError = (appError: AppError) => setError(appError);

    const onLog = (entry: { at: number; key: string; params?: Record<string, string | number> }) => {
      logSeq.current += 1;
      setLog((previous) => {
        const next = [...previous, { id: logSeq.current, ...entry }];
        // Log begrenzen, sonst waechst er ueber einen langen Abend endlos.
        return next.length > 60 ? next.slice(next.length - 60) : next;
      });
    };

    const onClosed = (reason: string) => {
      setClosedReason(reason);
      setState(null);
      clearResume();
    };

    // Fortschritt kommt als eigenes Event, damit nicht bei jedem
    // angereicherten Track der komplette Zustand uebertragen wird.
    const onProgress = (progress: EnrichmentProgress) => {
      setState((previous) => {
        if (!previous?.playlist) return previous;
        return { ...previous, playlist: { ...previous.playlist, progress } };
      });
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('room:state', onState);
    socket.on('app:error', onError);
    socket.on('room:log', onLog);
    socket.on('room:closed', onClosed);
    socket.on('playlist:progress', onProgress);

    if (socket.connected) onConnect();
    else socket.connect();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('room:state', onState);
      socket.off('app:error', onError);
      socket.off('room:log', onLog);
      socket.off('room:closed', onClosed);
      socket.off('playlist:progress', onProgress);
    };
  }, [socket]);

  // -------------------------------------------------------------------------
  // Aktionen
  // -------------------------------------------------------------------------

  const createRoom = useCallback(
    (name: string) =>
      withAckTimeout<{ ok: boolean; error?: AppError; code?: string }>(
        (settle) => {
          socket.emit('room:create', { name }, (res) => {
            if (res.ok) {
              clearError();
              saveResume(res.data);
              settle({ ok: true, code: res.data.code });
            } else {
              setError(res.error);
              settle({ ok: false, error: res.error });
            }
          });
        },
        () => {
          const timeout: AppError = { code: 'request_timeout' };
          setError(timeout);
          return { ok: false, error: timeout };
        },
      ),
    [socket, setError, clearError],
  );

  const joinRoom = useCallback(
    (code: string, name: string, asSpectator = false) =>
      withAckTimeout<{ ok: boolean; error?: AppError }>(
        (settle) => {
          socket.emit('room:join', { code, name, asSpectator }, (res) => {
            if (res.ok) {
              clearError();
              saveResume(res.data);
              settle({ ok: true });
            } else {
              setError(res.error);
              settle({ ok: false, error: res.error });
            }
          });
        },
        () => {
          const timeout: AppError = { code: 'request_timeout' };
          setError(timeout);
          return { ok: false, error: timeout };
        },
      ),
    [socket, setError, clearError],
  );

  const setPlaying = useCallback(
    (playing: boolean) => socket.emit('room:setPlaying', { playing }),
    [socket],
  );

  const leaveRoom = useCallback(() => {
    socket.emit('room:leave');
    clearResume();
    setState(null);
    setLog([]);
    // Neu verbinden, damit der Nutzer direkt ein neues Spiel starten kann.
    socket.disconnect();
    socket.connect();
  }, [socket]);

  const selectPlaylist = useCallback(
    (ref: string) =>
      withAckTimeout<{ ok: boolean; error?: AppError }>(
        (settle) => {
          socket.emit('host:selectPlaylist', { playlistRef: ref }, (res) => {
            if (res.ok) {
              // Erfolg raeumt eine alte Meldung weg -- sonst steht der
              // Fehler des vorigen Versuchs neben der geladenen Playlist.
              clearError();
              settle({ ok: true });
            } else {
              setError(res.error);
              settle({ ok: false, error: res.error });
            }
          });
        },
        () => {
          const timeout: AppError = { code: 'request_timeout' };
          setError(timeout);
          return { ok: false, error: timeout };
        },
      ),
    [socket, setError, clearError],
  );

  // ---------------------------------------------------------------------------
  // Einfache Sende-Aktionen
  //
  // Alle einzeln memoisiert und NICHT im Objektliteral erzeugt: Hooks
  // duerfen nicht in der Factory von `useMemo` aufgerufen werden.
  // ---------------------------------------------------------------------------

  const updateSettings = useCallback(
    (patch: Partial<GameSettings>) => socket.emit('host:settings', { settings: patch }),
    [socket],
  );
  const startGame = useCallback(() => socket.emit('host:start'), [socket]);
  const restartGame = useCallback(() => socket.emit('host:restart'), [socket]);
  const abortGame = useCallback(() => socket.emit('host:abort'), [socket]);
  const kick = useCallback(
    (playerId: string) => socket.emit('host:kick', { playerId }),
    [socket],
  );
  const advance = useCallback(() => socket.emit('host:advance'), [socket]);

  const addLocalPlayer = useCallback(
    (name: string) =>
      withAckTimeout<{ ok: boolean; error?: AppError }>(
        (settle) => {
          socket.emit('host:addLocalPlayer', { name }, (res) => {
            if (res.ok) settle({ ok: true });
            else {
              setError(res.error);
              settle({ ok: false, error: res.error });
            }
          });
        },
        () => ({ ok: false, error: { code: 'request_timeout' } }),
      ),
    [socket, setError],
  );

  const removeLocalPlayer = useCallback(
    (playerId: string) => socket.emit('host:removeLocalPlayer', { playerId }),
    [socket],
  );

  const place = useCallback(
    (slotIndex: number, onBehalfOf?: string) =>
      socket.emit('turn:place', { slotIndex, onBehalfOf }),
    [socket],
  );
  const guess = useCallback(
    (value: Guess, onBehalfOf?: string) => socket.emit('turn:guess', { ...value, onBehalfOf }),
    [socket],
  );
  const skip = useCallback(
    (onBehalfOf?: string) => socket.emit('turn:skip', { onBehalfOf }),
    [socket],
  );
  const replay = useCallback(() => socket.emit('turn:replay'), [socket]);
  const commit = useCallback(
    (onBehalfOf?: string) => socket.emit('turn:commit', { onBehalfOf }),
    [socket],
  );
  const claimSteal = useCallback(
    (slotIndex: number, onBehalfOf?: string) =>
      socket.emit('steal:claim', { slotIndex, onBehalfOf }),
    [socket],
  );
  const passSteal = useCallback(
    (onBehalfOf?: string) => socket.emit('steal:pass', { onBehalfOf }),
    [socket],
  );

  const registerAudio = useCallback(
    (deviceId: string) => socket.emit('audio:register', { deviceId }),
    [socket],
  );
  const unregisterAudio = useCallback(() => socket.emit('audio:unregister'), [socket]);
  const setAudioEnabled = useCallback(
    (enabled: boolean) => socket.emit('audio:setEnabled', { enabled }),
    [socket],
  );
  const syncSession = useCallback(() => socket.emit('session:sync'), [socket]);

  /**
   * Das zurueckgegebene Objekt MUSS memoisiert sein.
   *
   * Ohne `useMemo` bekommt es bei jedem Render eine neue Identitaet.
   * Jeder `useEffect`, der `api` in seinen Abhaengigkeiten hat, laeuft
   * dann bei jedem Render erneut -- und wenn dieser Effekt Zustand
   * setzt, entsteht eine Endlosschleife. Genau das ist passiert: der
   * Effekt, der den Spotify-Status laedt, hat den Browser mit
   * `/api/auth/status`-Anfragen geflutet, bis er mit
   * ERR_INSUFFICIENT_RESOURCES abbrach.
   */
  return useMemo<RoomApi>(
    () => ({
      state,
      connected,
      error,
      clearError,
      log,
      closedReason,

      createRoom,
      joinRoom,
      leaveRoom,
      setPlaying,

      updateSettings,
      selectPlaylist,
      startGame,
      restartGame,
      abortGame,
      kick,
      advance,
      addLocalPlayer,
      removeLocalPlayer,

      place,
      guess,
      skip,
      replay,
      commit,
      claimSteal,
      passSteal,

      registerAudio,
      unregisterAudio,
      setAudioEnabled,
      syncSession,
    }),
    [
      state,
      connected,
      error,
      clearError,
      log,
      closedReason,
      createRoom,
      joinRoom,
      leaveRoom,
      setPlaying,
      updateSettings,
      selectPlaylist,
      startGame,
      restartGame,
      abortGame,
      kick,
      advance,
      addLocalPlayer,
      removeLocalPlayer,
      place,
      guess,
      skip,
      replay,
      commit,
      claimSteal,
      passSteal,
      registerAudio,
      unregisterAudio,
      setAudioEnabled,
      syncSession,
    ],
  );
}

/** Praktische Ableitungen aus dem Zustand, damit Komponenten schlank bleiben. */
export function useRoomDerived(state: ClientRoomState | null) {
  return useMemo(() => {
    if (!state) return null;

    const you = state.players.find((player) => player.id === state.youId) ?? null;
    const active = state.players.find((player) => player.id === state.activePlayerId) ?? null;
    const isHost = state.hostId === state.youId;
    const isYourTurn = state.activePlayerId === state.youId;

    // Im Klau-Fenster wird in der EIGENEN Zeitleiste platziert,
    // sonst in der des aktiven Spielers.
    const stealing = state.phase === 'stealing';
    const yourClaim = state.stealClaims.find((claim) => claim.playerId === state.youId) ?? null;
    const youPassed = state.stealPasses.includes(state.youId);

    return { you, active, isHost, isYourTurn, stealing, yourClaim, youPassed };
  }, [state]);
}
